import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, MessageCircle, ShoppingBag, Zap, Users, Star, Trophy, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import IPPill from "@/components/IPPill";
import StatCard from "@/components/StatCard";
import SectionHeader from "@/components/SectionHeader";
import ServerCard from "@/components/ServerCard";
import EventCard from "@/components/EventCard";
import LivePlayerGrid from "@/components/LivePlayerGrid";
import ActivityFeed from "@/components/ActivityFeed";
import PostCard from "@/components/PostCard";
import { sfx } from "@/lib/sounds";
import {
  fetchServerStatus, fetchStats, fetchServers, fetchEvents, fetchBuilds, fetchLeaderboard, DISCORD_URL, fetchFeed,
} from "@/lib/api";
import AnimatedCounter from "@/components/AnimatedCounter";
import { SkeletonGrid, SkeletonCard, SkeletonBox } from "@/components/Skeletons";

const HERO_BG = "https://images.unsplash.com/photo-1662531914405-c037eab676b4";

export default function Home() {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [servers, setServers] = useState(null);
  const [events, setEvents] = useState(null);
  const [builds, setBuilds] = useState(null);
  const [topPvp, setTopPvp] = useState(null);
  const [feedPosts, setFeedPosts] = useState(null);

  useEffect(() => {
    fetchServerStatus().then(setStatus).catch(() => {});
    fetchStats().then(setStats).catch(() => {});
    fetchServers().then((d) => setServers(d || [])).catch(() => setServers([]));
    fetchEvents().then((d) => setEvents(d || [])).catch(() => setEvents([]));
    fetchBuilds().then((d) => setBuilds(d || [])).catch(() => setBuilds([]));
    fetchLeaderboard("pvp").then((d) => setTopPvp(d.entries || [])).catch(() => setTopPvp([]));
    fetchFeed({ tab: "trending", limit: 3 }).then((d) => setFeedPosts(d.posts || [])).catch(() => setFeedPosts([]));
    const id = setInterval(() => {
      fetchServerStatus().then(setStatus).catch(() => {});
      fetchStats().then(setStats).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const featured = (servers || []).filter((s) => s.is_featured);

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16" data-testid="hero-section">
        <motion.div
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.2, 0.9, 0.3, 1] }}
          className="absolute inset-0"
        >
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40" />
        </motion.div>

        <div className="pointer-events-none absolute inset-0 hidden md:block">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="float-block absolute rounded-md border border-[#FFBF00]/30 shadow-[0_0_30px_rgba(255,191,0,0.15)]"
              style={{
                left: `${10 + i * 18}%`,
                top: `${15 + (i % 3) * 20}%`,
                width: 36 + (i % 2) * 20,
                height: 36 + (i % 2) * 20,
                background: `linear-gradient(135deg, rgba(255,191,0,${0.1 + i * 0.05}), rgba(255,140,0,${0.05 + i * 0.03}))`,
                transform: `rotate(${i * 12}deg)`,
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${6 + i}s`,
                ["--rot"]: `${i * 12}deg`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 border-2 border-[#FFBF00]/40 text-[11px] uppercase tracking-[0.3em] blocky-shadow"
          >
            <span className={`w-2.5 h-2.5 rounded-full ${status?.online ? "bg-green-500 pulse-dot" : "bg-red-500"}`} />
            <span className="font-bold text-white">{status?.online ? "Network Online" : "Checking Network..."}</span>
            <span className="text-white/50">· {status?.version || "1.20.x"}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-6 font-hero text-6xl sm:text-7xl md:text-8xl leading-[0.9] tracking-tight max-w-5xl"
          >
            Welcome to <br className="hidden sm:block" />
            <span className="text-[#FFBF00] glow-text-yellow inline-block sticker-tilt">LOSTDC</span> Network
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-5 text-lg sm:text-xl text-white/70 max-w-2xl"
          >
            India&apos;s Ultimate Lifesteal Experience — steal hearts, build empires and outlast rivals across a network built by players, for players.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-play-now"
              onClick={() => {
                sfx.levelup();
                navigator.clipboard.writeText("play.lostdrkc.in").catch(() => {});
                window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#FFBF00] text-black font-bold uppercase tracking-wider hover:bg-[#FF8C00] border-2 border-black blocky-shadow-black hover-blocky transition-transform"
            >
              <Play size={16} fill="currentColor" /> Play Now
            </button>
            <a
              data-testid="hero-discord"
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => sfx.pling()}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-black/60 border-2 border-white/20 hover:border-[#FFBF00]/60 font-bold uppercase tracking-wider text-white hover-blocky transition-transform"
            >
              <MessageCircle size={16} /> Join Discord
            </a>
            <Link
              data-testid="hero-store"
              to="/store"
              onClick={() => sfx.click()}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:border-[#FFBF00]/40 font-semibold uppercase tracking-wider"
            >
              <ShoppingBag size={16} /> Visit Store
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <IPPill large />
            <div className="flex items-center gap-3 px-4 py-3 rounded-full glass">
              <Users size={16} className="text-[#FFBF00]" />
              <div className="font-mono text-sm">
                <AnimatedCounter value={status?.players_online || 0} />
                <span className="text-white/40"> / {status?.players_max || 500} online</span>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-black pointer-events-none" />
      </section>

      {/* ============ STATS ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Live Network"
            title="A Community That Never Sleeps"
            subtitle="Real-time stats across every LostDC server, updated continuously."
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard testId="stat-players-online" label="Players Online" value={stats?.players_online || 0} icon={Users} />
            <StatCard testId="stat-servers-online" label="Servers Online" value={stats?.servers_online || 0} icon={Zap} accent="#FF8C00" />
            <StatCard testId="stat-registered" label="Total Registered" value={stats?.total_registered || 0} />
            <StatCard testId="stat-discord" label="Discord Members" value={stats?.discord_members || 0} accent="#FF8C00" />
            <StatCard testId="stat-seasons" label="Seasons Completed" value={stats?.seasons_completed || 0} />
            <StatCard testId="stat-builds" label="Community Builds" value={stats?.total_builds || 0} accent="#FF8C00" />
          </div>
        </div>
      </section>

      {/* ============ FEATURED SERVERS ============ */}
      {featured.length > 0 && (
        <section className="relative py-16 px-4 sm:px-6" data-testid="featured-servers-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Star size={18} className="text-[#FFBF00]" />
              <div className="text-[11px] uppercase tracking-[0.4em] text-[#FFBF00]">Featured</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featured.map((s, i) => <ServerCard key={s.id} server={s} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* ============ ALL SERVERS ============ */}
      <section className="relative py-16 px-4 sm:px-6" data-testid="servers-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Servers" title="Pick Your Battlefield" subtitle="Four unique worlds, one network. Choose your playstyle." />
          {servers === null ? (
            <SkeletonGrid count={4} cols="md:grid-cols-2" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {servers.map((s, i) => <ServerCard key={s.id} server={s} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* ============ LIVE PLAYERS ============ */}
      <section className="relative py-20" data-testid="players-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-6">
          <SectionHeader eyebrow="Live · From Crafty" title="Players Online Now" subtitle="Aggregated live from every server through the Crafty control plane." />
        </div>
        <LivePlayerGrid />
      </section>

      {/* ============ EVENTS ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="events-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Active Events" title="Compete. Build. Win." subtitle="Tournaments, festivals and secret hunts happening every week." />
          {events === null ? (
            <SkeletonGrid count={4} cols="md:grid-cols-2" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {events.slice(0, 4).map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* ============ CREATORS ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="creators-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Creator Program"
            title="LostDC Creators"
            subtitle="Featured YouTubers, streamers and community stars — join or discover them here."
          />
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-2xl glass p-6 hover-lift flex flex-col justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Apply</div>
                <div className="font-hero text-3xl mt-2">Become a Creator</div>
                <p className="text-sm text-white/60 mt-2">Get a Creator badge, homepage feature, YouTube auto-sync and follower stats.</p>
              </div>
              <Link to="/creators/apply" onClick={() => sfx.pling()}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold self-start">
                Start Application
              </Link>
            </div>
            <div className="md:col-span-2 rounded-2xl glass p-6 hover-lift">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Featured Content</div>
              <div className="font-hero text-2xl mb-3">Latest Videos</div>
              <div className="text-sm text-white/60 mb-4">Videos tagged with <span className="font-mono text-[#FFBF00]">#lostdrkc #lostinlostdc #lostdcnetwork</span> auto-appear here.</div>
              <Link to="/creators" onClick={() => sfx.click()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-[#FFBF00]/50 text-xs uppercase tracking-widest">
                Open Creator Hub →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMMUNITY FEED ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="community-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <SectionHeader
              eyebrow="Community Feed"
              title="Fresh from the Network"
              subtitle="Builds, clips, questions and hot takes from the LostDC community."
            />
            <Link to="/community" onClick={() => sfx.click()} data-testid="home-community-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold whitespace-nowrap">
              <MessageSquare size={14} /> Open Community
            </Link>
          </div>
          {feedPosts === null ? (
            <div className="grid md:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : feedPosts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-4">
              {feedPosts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          ) : (
            <div className="rounded-2xl glass p-10 text-center text-white/60">
              Be the first to post — head over to the <Link to="/community" className="text-[#FFBF00] hover:underline">Community feed</Link>.
            </div>
          )}
        </div>
      </section>

      {/* ============ ACTIVITY + LEADERBOARD SNIPPET ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="activity-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl glass p-6">
            <div className="flex items-center gap-2 mb-5">
              <Trophy size={18} className="text-[#FFBF00]" />
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00]">Top PvP · This Season</div>
            </div>
            <div className="space-y-2">
              {topPvp === null ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-black/40 border border-white/5">
                    <SkeletonBox className="h-6 w-8" />
                    <SkeletonBox className="w-9 h-9 rounded" />
                    <SkeletonBox className="h-3 flex-1" />
                    <SkeletonBox className="h-3 w-20" />
                  </div>
                ))
              ) : (topPvp.slice(0, 5).map((e) => (
                <div key={e.rank} className="flex items-center gap-4 p-3 rounded-lg bg-black/40 border border-white/5">
                  <div className="font-hero text-2xl w-10 text-[#FFBF00]">#{e.rank}</div>
                  <img src={`https://mc-heads.net/avatar/${e.username}/64`} alt={e.username} className="w-9 h-9 rounded" loading="lazy" />
                  <div className="flex-1 font-semibold">{e.username}</div>
                  <div className="font-mono text-[#FFBF00]">{e.value.toLocaleString("en-IN")} <span className="text-white/40 text-xs">{e.unit}</span></div>
                </div>
              )))}
            </div>
            <div className="mt-4">
              <Link to="/leaderboards" onClick={() => sfx.click()} className="text-xs uppercase tracking-widest text-[#FFBF00] hover:underline">View full leaderboards →</Link>
            </div>
          </div>
          <ActivityFeed />
        </div>
      </section>

      {/* ============ BUILDS PREVIEW ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="builds-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Gallery" title="Community Masterpieces" subtitle="A snapshot of what our community has built together across seasons." />
          <div className="masonry">
            {(builds || []).map((b) => (
              <div key={b.id} className="relative rounded-xl overflow-hidden group border border-white/10">
                <img src={b.image} alt={b.title} className="w-full block group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]/90">{b.category}</div>
                  <div className="font-hero text-xl leading-none mt-1">{b.title}</div>
                  <div className="text-[11px] text-white/60 mt-1">by {b.author}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/builds" onClick={() => sfx.click()} data-testid="home-builds-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:border-[#FFBF00]/50 text-sm uppercase tracking-wider">
              Explore Full Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* ============ DISCORD ============ */}
      <section className="relative py-24 px-4 sm:px-6" data-testid="discord-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center">
          <div>
            <SectionHeader eyebrow="Discord" title="Join the Crew" subtitle="12,000+ members. Daily announcements, event pings, giveaways and the friendliest Minecraft India community." />
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => sfx.pling()}
              data-testid="discord-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold uppercase tracking-wider text-sm"
            >
              <MessageCircle size={16} /> Join Discord Server
            </a>
          </div>
          <div className="relative rounded-2xl overflow-hidden glass p-8">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#5865F2]/30 blur-3xl" />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00]">Latest Announcement</div>
              <div className="mt-2 font-hero text-2xl">Season 8 goes live — 15th Feb</div>
              <p className="text-sm text-white/60 mt-2">New economy, custom quests, PvP arenas and a brand new spawn built by the community.</p>
              <div className="mt-6 flex items-center gap-6 text-sm">
                <div>
                  <div className="font-hero text-3xl text-[#FFBF00]">{(stats?.discord_members || 12480).toLocaleString("en-IN")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Members</div>
                </div>
                <div>
                  <div className="font-hero text-3xl text-[#FFBF00]">1.2K</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Online Now</div>
                </div>
                <div>
                  <div className="font-hero text-3xl text-[#FFBF00]">45+</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Channels</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
